// Level — the sim slice of game/Level.cpp: level info (sides, player counts), the
// terrain/fog maps, areas, and the GameObjects/Galaxite instantiation walk. The tile map
// (art), intro/Ecom text, and trigger/unit-group EXECUTION are out of this slice:
// triggers and unit groups port at M3 (their raw sections are retained for it); the
// Overmind arm throws until Overmind.cpp ports (no kfPlrComputerOvermind player can
// exist before then).

import type { SimContext } from './context.ts';
import { IniReader, IniScanf, type IniSection } from './data/ini.ts';
import { TerrainMap } from './terrain/terrainmap.ts';
import { FogMap } from './sim/fogmap.ts';
import { CreateGob } from './gob/factory.ts';
import './gob/alltypes.ts'; // gob-type registrations
import { kgtOvermind } from './gob/gobtypes.ts';
import type { OvermindGob } from './gob/overmind.ts';
import { WcFromTc } from './math/coords.ts';
import {
  kcSides,
  kside1,
  kfPlrComputer,
  kfPlrComputerOvermind,
  kfPlrUnfulfilled,
} from './player/side.ts';
import { kfGobUnit } from './gob/gob.ts';
import type { UnitGob } from './gob/unitgob.ts';
import type { Simulation } from './sim/simulation.ts';
import { TriggerMgr } from './trigger/triggermgr.ts';
import { UnitGroupMgr } from './unitgroupmgr.ts';
import { gcStructGobsComputerLimitSP } from './gob/gobmgr.ts';
import type { WPoint } from './types.ts';

// mpshared/side.h:13-16
export const knIntelligenceHuman = 0;
export const knIntelligenceComputer = 1;
export const knIntelligenceComputerOvermind = 2;
export const knIntelligenceComputerNeutral = 3;

// game/ht.h:5336-5338 — non-68K build
export const kcpgobMax = 768;

// game/ht.h:5436-5443 SideInfo
export interface SideInfo {
  nInitialCredits: number;
  wptInitialView: WPoint;
  nIntelligence: number;
  cStructuresInitial: number;
  cMobileUnitsInitial: number;
}

/** The level-vector input: raw ordered .lvl sections (cini-extracted) + the trmap. */
export interface LevelData {
  sections: IniSection[];
  trmap: { ctx: number; cty: number; terrain: string }; // terrain = base64 bytes
}

export class Level {
  private ctx: SimContext;
  m_asidi: SideInfo[] = [];
  m_nPlayersMin = 1;
  m_nPlayersMax = 1;
  m_szTitle = '';
  m_szFileLevel = '';
  m_nVersion = 0;
  m_dwRevision = 0; // [General] Revision (savegame staleness check)
  m_ptrmap: TerrainMap | null = null;
  m_pfogm: FogMap | null = null;
  m_ugm: UnitGroupMgr;
  m_tgrm: TriggerMgr;

  constructor(ctx: SimContext) {
    this.ctx = ctx;
    this.m_tgrm = new TriggerMgr(ctx);
    this.m_ugm = new UnitGroupMgr(ctx);
  }

  GetTerrainMap(): TerrainMap {
    return this.m_ptrmap!;
  }
  GetFogMap(): FogMap {
    return this.m_pfogm!;
  }
  GetSideInfo(side: number): SideInfo {
    return this.m_asidi[side];
  }
  GetUnitGroupMgr(): UnitGroupMgr {
    return this.m_ugm;
  }
  GetTriggerMgr(): TriggerMgr {
    return this.m_tgrm;
  }
  GetVersion(): number {
    return this.m_nVersion;
  }

  // game/Level.cpp:40-125
  LoadLevelInfo(pszLevelName: string, pini: IniReader): boolean {
    this.m_nVersion = pini.GetInt('General', 'Version') ?? 0;
    this.m_dwRevision = (pini.GetInt('General', 'Revision') ?? 0) >>> 0;

    this.m_szTitle = pini.GetString('General', 'Title') ?? '<untitled>';

    this.m_nPlayersMin = pini.GetInt('General', 'MinPlayers') ?? 0;
    this.m_nPlayersMax = pini.GetInt('General', 'MaxPlayers') ?? 0;

    // Normalize: multiplayer level filenames start "m_"
    if (pszLevelName[0] === 'm' && pszLevelName[1] === '_') {
      if (this.m_nPlayersMin < 2) this.m_nPlayersMin = 2;
      if (this.m_nPlayersMax > 4) this.m_nPlayersMax = 4;
      if (this.m_nPlayersMax < this.m_nPlayersMin) this.m_nPlayersMax = this.m_nPlayersMin;
    } else {
      this.m_nPlayersMin = 1;
      this.m_nPlayersMax = 1;
    }

    for (let side = 0; side < kcSides; side++) {
      const szSideName = side === 0 ? 'sideNeutral' : `side${side}`;
      this.m_asidi[side] = this.LoadSideInfo(pini, szSideName);
    }

    // If there is no human side, assume side 1 (gpplrLocal must be set to something)
    let fHumanFound = false;
    for (let side = 0; side < kcSides; side++) {
      if (this.m_asidi[side].nIntelligence === knIntelligenceHuman) fHumanFound = true;
    }
    if (!fHumanFound) this.m_asidi[kside1].nIntelligence = knIntelligenceHuman;

    return true;
  }

  // game/Level.cpp:127-146
  LoadSideInfo(pini: IniReader, pszSideName: string): SideInfo {
    const psidi: SideInfo = {
      nInitialCredits: 5000,
      wptInitialView: { wx: 0, wy: 0 },
      nIntelligence: knIntelligenceComputer,
      cStructuresInitial: 0,
      cMobileUnitsInitial: 0,
    };
    psidi.nInitialCredits = pini.GetInt(pszSideName, 'InitialCredits') ?? psidi.nInitialCredits;
    const view = pini.GetPropertyValue(pszSideName, 'InitialView', '%d,%d');
    if (view.count === 2) {
      psidi.wptInitialView.wx = WcFromTc(view.values[0] as number);
      psidi.wptInitialView.wy = WcFromTc(view.values[1] as number);
    }
    psidi.nIntelligence = pini.GetInt(pszSideName, 'Intelligence') ?? psidi.nIntelligence;
    psidi.cStructuresInitial = pini.GetInt(pszSideName, 'InitialStructureCount') ?? 0;
    psidi.cMobileUnitsInitial = pini.GetInt(pszSideName, 'InitialMobileUnitCount') ?? 0;
    return psidi;
  }

  // game/Level.cpp:148-184 + 193-319 — Init(level) -> constants + variables. The data
  // arrives pre-extracted (no PakReader): the trmap bytes and the raw .lvl sections.
  Init(pszLevel: string, data: LevelData, fConstantsOnly = false): boolean {
    this.m_szFileLevel = pszLevel;
    // Simulation::LoadLevel sets gsim's level pointer BEFORE Level::Init runs (the
    // trigger-condition constructors read gsim.GetLevel() during the load)
    if (this.ctx.sim !== null) {
      (this.ctx.sim as unknown as { m_plvl: unknown }).m_plvl = this;
    }
    // Replicator clones join the original's UnitGroup (Replicator.cpp via
    // gsim.GetLevel()->GetUnitGroupMgr()->GetUnitGroup)
    this.ctx.unitGroupOfGob = (gid: number) => this.m_ugm.GetUnitGroup(gid);
    const pini = new IniReader(data.sections);

    if (!this.LoadLevelConstants(pszLevel, pini, data)) return false;

    // game/Level.cpp:168-170 — establish the SP unit-count deltas
    CalcUnitCountDeltas(this.ctx, this);

    // fConstantsOnly = the savegame path: gobs/galaxite come from the stream instead
    if (!fConstantsOnly) {
      if (!this.LoadLevelVariables(pini)) return false;
    }
    return true;
  }

  // game/Level.cpp:403-478 (knVerLevelState 2) — re-runs Init(constants-only) for the
  // named level, then restores areas, fog/galaxite, terrain occupancy, every savable
  // gob (CreateGob by type + LoadState, which re-registers at the saved gid), triggers
  // and unit groups. levelDataFor supplies the level vector (no pak reader in TS).
  LoadState(
    pstm: import('./sm/stream.ts').Stream,
    levelDataFor?: (szLevel: string) => LevelData,
  ): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;

    const szLevel = pstm.ReadString(29 /* kcbFilename */);
    const dataFor = levelDataFor ?? this.ctx.levelDataFor;
    if (dataFor === undefined) throw new Error('Level.LoadState: no levelDataFor source');
    if (!this.Init(szLevel, dataFor(szLevel), true)) return false;

    const dwRevision = pstm.ReadDword();
    if (dwRevision !== this.m_dwRevision) return false; // stale savegame vs revised level

    if (!this.ctx.gobm.LoadState(pstm)) return false;
    if (!this.m_pfogm!.LoadState(pstm)) return false;
    if (!this.m_ptrmap!.LoadState(pstm)) return false;

    let cGobs = pstm.ReadWord();
    while (cGobs-- !== 0) {
      // dev hook (mirrors the SaveState hook; host-carried so embedders can arm it
      // through Sim.load opts): record boundaries for the cross-validation masker
      (this.ctx.host as unknown as { loadGobOffsets?: number[] }).loadGobOffsets?.push(
        (pstm as unknown as { tell?: () => number }).tell?.() ?? -1,
      );
      const gt = pstm.ReadByte();
      const pgob = CreateGob(this.ctx, gt);
      if (pgob === null) return false;
      if (!(pgob as unknown as { LoadState(p: unknown): boolean }).LoadState(pstm)) return false;
    }
    (this.ctx.host as unknown as { loadGobOffsets?: number[] }).loadGobOffsets?.push(
      (pstm as unknown as { tell?: () => number }).tell?.() ?? -1,
    );

    // issue #96 B23: relink the free chain to the saver's order (no-op for a faithful load).
    this.ctx.gobm.ApplyLoadedFreeList();
    // issue #96: restore the saver's exact per-tile gidMap chain order (no-op for faithful).
    this.ctx.gobm.ApplyLoadedGidMap();
    // issue #96 / #98: re-apply the saved per-player m_cStructsNeedCredits over the gob-load
    // re-registration that would otherwise double-count it (no-op for a faithful load).
    this.ctx.plrm.ApplyLoadedAccumulators();

    if (!this.m_tgrm.LoadState(pstm)) return false;
    if (!this.m_ugm.LoadState(pstm)) return false;

    return pstm.IsSuccess();
  }

  // game/Level.cpp:480-520
  SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteString(this.m_szFileLevel);
    pstm.WriteDword(this.m_dwRevision);
    this.ctx.gobm.SaveState(pstm);
    this.m_pfogm!.SaveState(pstm);
    this.m_ptrmap!.SaveState(pstm);

    const savable = [];
    for (
      let pgobT = this.ctx.gobm.GetFirstGob();
      pgobT !== null;
      pgobT = this.ctx.gobm.GetNextGob(pgobT)
    ) {
      if (pgobT.IsSavable()) savable.push(pgobT);
    }
    pstm.WriteWord(savable.length);

    // FAITHFUL: emit head-first (newest→oldest). The C++ load re-creates head-first and
    // AddGob prepends each, REVERSING the list — a loaded game then walked gobs (update
    // order, per-tile gidMap chains) in the opposite order and lawfully diverged (sim-quirks
    // B1; SP saves never compare across a load so it's invisible there).
    // snapshotComplete (issue #96): emit TAIL-first (oldest→newest = original AddGob/creation
    // order) so the load path's prepend rebuilds the identical head chain AND re-runs AddGob
    // in creation order, reproducing the per-tile gidMap chains. → bit-exact resume.
    const lo = this.ctx.snapshotComplete ? savable.length - 1 : 0;
    const hi = this.ctx.snapshotComplete ? -1 : savable.length;
    const step = this.ctx.snapshotComplete ? -1 : 1;
    for (let i = lo; i !== hi; i += step) {
      const pgobT = savable[i];
      // dev hook: cross-validation tooling records [offset, type, gid] per record
      (this.ctx as unknown as { saveGobOffsets?: [number, number, number][] }).saveGobOffsets?.push(
        [
          (pstm as unknown as { tellWrite?: () => number }).tellWrite?.() ?? -1,
          pgobT.GetType(),
          pgobT.GetId(),
        ],
      );
      pstm.WriteByte(pgobT.GetType());
      (pgobT as unknown as { SaveState(p: unknown): boolean }).SaveState(pstm);
    }
    // final sentinel for the dev hook (mirrors the LoadState hook): last record's end
    (this.ctx as unknown as { saveGobOffsets?: [number, number, number][] }).saveGobOffsets?.push([
      (pstm as unknown as { tellWrite?: () => number }).tellWrite?.() ?? -1,
      -1,
      -1,
    ]);

    this.m_tgrm.SaveState(pstm);
    this.m_ugm.SaveState(pstm);
    return pstm.IsSuccess();
  }

  // game/Level.cpp:193-319
  LoadLevelConstants(pszLevelName: string, pini: IniReader, data: LevelData): boolean {
    if (!this.LoadLevelInfo(pszLevelName, pini)) return false;

    // (TileMap load is render; the map dimensions come from the terrain map, which the
    // C++ asserts equal to the tile map's.)

    // Init GobMgr
    if (!this.ctx.gobm.Init(data.trmap.ctx, data.trmap.cty, kcpgobMax)) return false;

    // Load terrain map
    this.m_ptrmap = new TerrainMap();
    const terrain = Uint8Array.from(Buffer.from(data.trmap.terrain, 'base64'));
    if (!this.m_ptrmap.Init(data.trmap.ctx, data.trmap.cty, terrain)) return false;
    this.ctx.trmap = this.m_ptrmap;

    // Create fog map
    this.m_pfogm = new FogMap();
    if (!this.m_pfogm.Init(data.trmap.ctx, data.trmap.cty)) return false;
    // the C++ gpmm reaches inside FogMap (fogmap.cpp:180/265/321/577/596) ride
    // the presentation host (no-ops headless; the host OBJECT is stable —
    // Sim.load Object.assigns embedder seams onto it)
    this.m_pfogm.host = this.ctx.host;
    this.ctx.fogm = this.m_pfogm;

    // Instantiate an OvermindGob for each Computer Player (game/Level.cpp:262-300)
    for (
      let pplr = this.ctx.plrm.GetNextPlayer(null);
      pplr !== null;
      pplr = this.ctx.plrm.GetNextPlayer(pplr)
    ) {
      if (!(pplr.GetFlags() & kfPlrComputerOvermind)) continue;

      const pgobOvermind = CreateGob(this.ctx, kgtOvermind) as OvermindGob | null;
      if (pgobOvermind === null) return false;
      if (!pgobOvermind.InitOvermind()) return false;
      pgobOvermind.SetOwner(pplr);
    }

    // Load the Triggers (game/Level.cpp:302-307)
    if (!this.m_tgrm.Init(pini)) return false;

    // Load the UnitGroups (game/Level.cpp:310-316)
    if (!this.m_ugm.Init(pini)) return false;

    return true;
  }

  // game/Level.cpp:321-401
  LoadLevelVariables(pini: IniReader): boolean {
    // Load areas (GobMgr::LoadAreas walks the section in C++; parse "%d,%d,%d,%d" here)
    const areasSec = pini.GetSection('Areas');
    const areas: { left: number; top: number; width: number; height: number }[] = [];
    if (areasSec !== null) {
      for (const p of areasSec.props) {
        const r = IniScanf(p.value, '%d,%d,%d,%d');
        if (r.count !== 4) return false;
        const [left, top, width, height] = r.values as number[];
        areas.push({ left, top, width, height });
      }
    }
    if (!this.ctx.gobm.LoadAreas(areas)) return false;

    // Enumerate all Gob descriptions and instantiate in-memory versions
    const goSec = pini.GetSection('GameObjects');
    if (goSec !== null) {
      for (const prop of goSec.props) {
        const head = IniScanf(prop.value, '%d,');
        if (head.count < 1) return false;
        const gt = head.values[0] as number;

        const pgob = CreateGob(this.ctx, gt);
        if (pgob === null) return false;

        const pszName = prop.key === 'nil' ? null : prop.key;
        if (pszName !== null) pgob.gobName = pszName;

        if (!(pgob as unknown as { InitFromArgs(v: string): boolean }).InitFromArgs(prop.value))
          return false;
      }
    }

    // Enumerate all Galaxite positions into the fog/galaxite map
    const gxSec = pini.GetSection('Galaxite');
    if (gxSec !== null) {
      for (const prop of gxSec.props) {
        const r = IniScanf(prop.value, '%d,%d,%d');
        if (r.count !== 3) return false;
        const [nGx, tx, ty] = r.values as number[];
        this.m_pfogm!.SetGalaxite(nGx, tx, ty);
      }
    }

    // Instantiate the "CreateAtLevelLoad" UnitGroups (game/Level.cpp:396-398)
    this.m_ugm.CreateAtLevelLoadGroups();

    return true;
  }
}

// game/Simulation.cpp:229-296 — Simulation::LoadLevel minus the Level construction
// (the level data arrives pre-extracted). Per player: initial credits + upgrades from
// the side info, then remove every unit owned by unfulfilled dummy players.
// game/game.cpp:578-606 Game::CalcUnitCountDeltas — SP only: hand the computer's
// unused structure headroom (limit - initial - 5) to the human player via the ctx
// deltas IsBelowLimit consults. MP ignores the deltas entirely.
export function CalcUnitCountDeltas(ctx: SimContext, plvl: Level): void {
  let cComputerStructuresInitial = 0;
  for (let side = 0; side < kcSides; side++) {
    const pplr = ctx.plrm.GetPlayer(side);
    if (pplr !== null && pplr.GetFlags() & kfPlrComputer) {
      cComputerStructuresInitial += plvl.GetSideInfo(side).cStructuresInitial;
    }
  }

  // HACK ALERT (theirs): give the computer room to build 5 structures
  const cStructuresAvailable = gcStructGobsComputerLimitSP - cComputerStructuresInitial - 5;
  if (cStructuresAvailable > 0) {
    ctx.cStructGobsComputerDeltaSP = -cStructuresAvailable;
    ctx.cStructGobsHumanDeltaSP = cStructuresAvailable;
  } else {
    ctx.cStructGobsComputerDeltaSP = 0;
    ctx.cStructGobsHumanDeltaSP = 0;
  }
}

export function BindLevelToSimulation(ctx: SimContext, sim: Simulation, plvl: Level): void {
  sim.m_plvl = plvl;
  // Simulation::PerLevelInit (the per-level BuildMgr's birth + counter resets)
  // happens in Sim.create BEFORE Level.Init, matching game.cpp:1995 → 2010 —
  // an at-level-load UnitGroup::Activate queues into that BuildMgr, so it must
  // NOT be re-created here (the queue would drop).

  for (
    let pplr = ctx.plrm.GetNextPlayer(null);
    pplr !== null;
    pplr = ctx.plrm.GetNextPlayer(pplr)
  ) {
    const psidi = plvl.GetSideInfo(pplr.GetSide());
    pplr.SetCredits(psidi.nInitialCredits, false);
    pplr.SetUpgrades(0);

    // (the gpplrLocal view centering is presentation)

    if (pplr.GetFlags() & kfPlrUnfulfilled) {
      let pgobT = ctx.gobm.GetFirstGob();
      while (pgobT !== null) {
        const pgobNext = ctx.gobm.GetNextGob(pgobT);
        if (pgobT.GetOwner() === pplr) {
          if (pgobT.GetFlags() & kfGobUnit) {
            const punt = pgobT as UnitGob;
            punt.Deactivate();
            punt.Delete();
            pplr.DecUnitBuiltCount(punt.GetUnitType());
          } else {
            ctx.gobm.RemoveGob(pgobT);
          }
        }
        pgobT = pgobNext;
      }
    }
  }
}
